import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate, useLocation } from 'react-router-dom';
import { createRecipe } from '../services/recipesService';
import { deleteNotification } from '../services/notificationsService';

export const RecipeSharePage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = location.state?.notification;

  const [saving, setSaving] = useState(false);

  if (!notification || !notification.metadata?.recipeData) {
    navigate('/notifications');
    return null;
  }

  const recipeData = notification.metadata.recipeData;

  const handleAccept = async () => {
    if (!user?.uid) return;

    setSaving(true);
    try {
      await createRecipe(user.uid, {
        ...recipeData,
        userId: user.uid,
        sharedBy: notification.senderName || notification.metadata?.senderName || 'Amigo',
        sharedByUserId: notification.senderId || notification.metadata?.senderId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      if (notification.id) {
        await deleteNotification(notification.id);
      }

      navigate('/recipes');
    } catch (error) {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (notification.id) {
      await deleteNotification(notification.id);
    }
    navigate('/notifications');
  };

  return (
    <>
      <TopBar
        title="Receita Compartilhada"
        showBackButton
        onBackClick={() => navigate('/notifications')}
      />

      <Box sx={{
        backgroundColor: theme.colors.background,
        minHeight: '100vh',
        paddingBottom: '120px',
        paddingTop: '70px',
      }}>
        <Container sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: theme.radii.medium,
              backgroundColor: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <Typography variant="h6" sx={{ color: theme.colors.text.primary, mb: 1 }}>
              {recipeData.title}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
              De: {notification.senderName}
            </Typography>

            {recipeData.imageUrl && (
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  borderRadius: theme.radii.medium,
                  backgroundImage: `url(${recipeData.imageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  mb: 2,
                }}
              />
            )}

            <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, fontWeight: 'bold', mt: 2, mb: 1 }}>
              Porções: {recipeData.servings}
            </Typography>

            <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, fontWeight: 'bold', mb: 1 }}>
              Tempo: {recipeData.prepTime} min
            </Typography>

            <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, fontWeight: 'bold', mt: 2, mb: 1 }}>
              Ingredientes:
            </Typography>
            {recipeData.ingredients.map((ing: any, index: number) => (
              <Typography key={index} variant="body2" sx={{ color: theme.colors.text.secondary, mb: 0.5 }}>
                - {ing.quantity} {ing.unit} de {ing.name}
              </Typography>
            ))}

            <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, fontWeight: 'bold', mt: 2, mb: 1 }}>
              Modo de preparo:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, whiteSpace: 'pre-wrap' }}>
              {recipeData.instructions}
            </Typography>

            {recipeData.description && (
              <>
                <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, fontWeight: 'bold', mt: 2, mb: 1 }}>
                  Observações:
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  {recipeData.description}
                </Typography>
              </>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReject}
                disabled={saving}
                sx={{
                  borderColor: theme.colors.border,
                  color: theme.colors.text.primary,
                  '&:hover': {
                    borderColor: theme.colors.border,
                    backgroundColor: `${theme.colors.border}30`,
                  }
                }}
              >
                Recusar
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAccept}
                disabled={saving}
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.text.contrast,
                  '&:hover': {
                    backgroundColor: theme.colors.primary,
                    opacity: 0.9,
                  }
                }}
              >
                {saving ? <CircularProgress size={20} sx={{ color: theme.colors.text.contrast }} /> : 'Adicionar ao meu livro'}
              </Button>
            </Box>
          </Paper>
        </Container>

        <BottomNavigation />
      </Box>
    </>
  );
};
